import type { CSSProperties } from "react";

interface Props {
  // Values present in the table for this column, each with a count.
  values: Array<{ value: string; count: number }>;
  // Currently allowed set (empty/null = no filter, show all).
  selected: Set<string> | null;
  onChange: (next: Set<string> | null) => void;
  onClose: () => void;
  // Viewport coordinates supplied by the caller, which portals this into the
  // body so the table's scroll pane can't clip it.
  style?: CSSProperties;
}

// Popover anchored under a column header. Shows checkboxes for every unique
// value seen in the table for that column. "(empty)" represents missing/blank
// cell values. Untouched (selected = null) means no filter; explicit empty set
// means "filter out everything" (which would hide all rows).
export function FilterPopover({
  values,
  selected,
  onChange,
  onClose,
  style,
}: Props) {
  const isFiltered = selected !== null;

  function toggle(v: string) {
    const next = new Set(selected ?? values.map((x) => x.value));
    if (next.has(v)) next.delete(v);
    else next.add(v);
    // If the user re-included everything, treat as "no filter".
    const allValues = values.map((x) => x.value);
    if (next.size === allValues.length && allValues.every((x) => next.has(x))) {
      onChange(null);
    } else {
      onChange(next);
    }
  }

  function selectAll() {
    onChange(null);
  }

  function selectNone() {
    onChange(new Set());
  }

  return (
    <div
      className="fixed z-50 bg-white rounded-xl ring-1 ring-slate-200 shadow-lg p-3 w-64"
      style={style}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">
          Filter
        </span>
        <div className="flex items-center gap-2 text-[10px]">
          <button
            onClick={selectAll}
            className="text-blue-600 hover:underline"
            disabled={!isFiltered}
          >
            All
          </button>
          <button
            onClick={selectNone}
            className="text-slate-400 hover:text-slate-700"
          >
            None
          </button>
        </div>
      </div>
      <div className="max-h-64 overflow-y-auto -mx-3 px-3">
        {values.length === 0 && (
          <p className="text-xs text-slate-400 italic py-2">No values to filter.</p>
        )}
        {values.map(({ value, count }) => {
          const isChecked = !isFiltered || (selected !== null && selected.has(value));
          const displayValue = value === "" ? "(empty)" : value;
          return (
            <label
              key={value}
              className="flex items-center gap-2 py-1 cursor-pointer hover:bg-slate-50 rounded px-1 text-xs"
            >
              <input
                type="checkbox"
                checked={isChecked}
                onChange={() => toggle(value)}
                className="cursor-pointer"
              />
              <span className="flex-1 text-slate-700 truncate">{displayValue}</span>
              <span className="text-slate-400 tabular-nums">{count}</span>
            </label>
          );
        })}
      </div>
      <div className="mt-2 pt-2 border-t border-slate-100 flex justify-end">
        <button
          onClick={onClose}
          className="text-xs text-slate-500 hover:text-slate-700"
        >
          Close
        </button>
      </div>
    </div>
  );
}
